import { useEffect, useRef } from 'react';

interface UseClickOutsideOptions {
  enabled?: boolean;
  delay?: number;
  excludeSelectors?: string[];
}

export const useClickOutside = <T extends HTMLElement>(
  callback: () => void,
  options: UseClickOutsideOptions = {}
) => {
  const { enabled = true, delay = 0, excludeSelectors = [] } = options;
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is inside the ref element
      if (ref.current && ref.current.contains(target)) {
        return;
      }

      // Check if click is on any excluded elements
      const isExcluded = excludeSelectors.some(selector => {
        const excludedElement = document.querySelector(selector);
        return excludedElement && excludedElement.contains(target);
      });

      if (isExcluded) {
        return;
      }

      callback();
    };

    if (delay > 0) {
      // Use a delay to prevent immediate closing after opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, delay);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } else {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [callback, enabled, delay, excludeSelectors]);

  return ref;
};
